import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedShoppingList() {
  const { token } = useParams();
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/shopping-lists/share/${token}`);
        setList(data);
      } catch {
        setErr("This shared list link isn't valid — it may have been revoked. Ask whoever sent it to share a fresh link.");
      }
    })();
  }, [token]);

  const toggle = async (name, checked) => {
    if (!name) return;
    setBusy((b) => ({ ...b, [name]: true }));
    // optimistic update
    setList((cur) => cur ? ({
      ...cur,
      items: cur.items.map((it) => it.name === name ? { ...it, checked } : it),
      ticked_items: cur.items.filter((it) => it.name === name ? checked : it.checked).length,
    }) : cur);
    try {
      const { data } = await axios.post(`${API}/shopping-lists/share/${token}/toggle`, { item_name: name, checked });
      setList(data);
    } catch {
      // revert
      setList((cur) => cur ? ({
        ...cur,
        items: cur.items.map((it) => it.name === name ? { ...it, checked: !checked } : it),
      }) : cur);
    } finally {
      setBusy((b) => ({ ...b, [name]: false }));
    }
  };

  if (err) {
    return (
      <div className="min-h-screen bg-[#F5F1E8] flex items-center justify-center px-4">
        <div className="max-w-md text-center ff-card p-6">
          <div className="text-3xl mb-2">🔗</div>
          <h1 className="text-xl font-semibold mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Link no longer works</h1>
          <p className="text-sm text-[#5C6B61]">{err}</p>
        </div>
      </div>
    );
  }

  if (!list) {
    return <div className="min-h-screen bg-[#F5F1E8] flex items-center justify-center text-[#5C6B61]">Loading list…</div>;
  }

  const remaining = list.total_items - list.ticked_items;

  return (
    <div className="min-h-screen bg-[#F5F1E8] py-8 px-4" data-testid="shared-list-page">
      <div className="max-w-xl mx-auto space-y-6">
        <header className="text-center">
          <div className="text-xs uppercase tracking-widest text-[#8B9690] mb-2">Shared shopping list</div>
          <h1 className="text-3xl font-semibold" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="shared-list-name">
            {list.name}
          </h1>
          <p className="text-sm text-[#5C6B61] mt-2">
            {list.shared_by} sent you this list · <span className="font-medium text-[#2A332C]" data-testid="shared-list-remaining">{remaining}</span> of {list.total_items} still to grab
          </p>
        </header>

        <div className="ff-card p-4 space-y-1" data-testid="shared-list-items">
          {list.items.map((it) => (
            <label
              key={it.name}
              className={`flex items-center gap-3 p-2.5 rounded-lg hover:bg-[#F3F0EA] cursor-pointer transition ${it.checked ? "opacity-50" : ""}`}
              data-testid={`shared-item-${it.name}`}
            >
              <input
                type="checkbox"
                checked={!!it.checked}
                disabled={!!busy[it.name]}
                onChange={(e) => toggle(it.name, e.target.checked)}
                className="w-5 h-5 accent-[#3B5A3E]"
              />
              <div className="flex-1 min-w-0">
                <div className={`text-sm ${it.checked ? "line-through" : "font-medium text-[#2A332C]"}`}>
                  {it.name}{it.quantity ? ` — ${it.quantity}` : ""}
                </div>
              </div>
            </label>
          ))}
        </div>

        <div className="text-center text-xs text-[#8B9690] space-y-2">
          <p>Ticks sync live back to {list.shared_by}. No login needed.</p>
          <Link to="/" className="inline-block text-[#3B5A3E] hover:underline font-medium">
            Try Shop, Cal & Meal — snap your fridge, get meal ideas →
          </Link>
        </div>
      </div>
    </div>
  );
}
